import { useMemo } from 'react';
import { usePolicies } from '../../../../hooks/api';
import type { Policy, PolicyType } from '../../../../types';

/**
 * Replacement for the old PolicyModule.getGroupedPolicies selector in Redux.
 * This hook uses React Query to fetch the policies and group them by type.
 *
 * @param {boolean} shouldOmitRules - Whether to omit the rules from the policies.
 * @param {boolean} shouldFetch - Whether to fetch the policies.
 */
export default function useGroupedPolicies(
  shouldOmitRules: boolean = true,
  shouldFetch: boolean = true,
) {
  const { data: policies, ...rest } = usePolicies(shouldOmitRules, shouldFetch);

  return {
    groupedPolicies: useMemo(() => {
      if (!policies) {
        return new Map<PolicyType, Policy[]>();
      }
      const groupedPolicies = policies.reduce<Map<PolicyType, Policy[]>>((acc, policy) => {
        const policiesByType = acc.get(policy.type) ?? [];
        policiesByType.push(policy);
        acc.set(policy.type, policiesByType);
        return acc;
      }, new Map<PolicyType, Policy[]>());
      //  Sort each group
      groupedPolicies.forEach((policiesByType) => {
        policiesByType.sort((a, b) => a.title.localeCompare(b.title));
      });

      return groupedPolicies;
    }, [policies]),
    ...rest,
  };
}
